import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { locationsRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { Privileges } from 'src/app/privileges/privileges';
import { e911Changed } from 'src/app/signalr/e911-hub/e911-hub.actions';
import { E911Hub } from 'src/app/signalr/e911-hub/e911-hub.service';
import { GetLocationDetail, GetLocationDetailVariables } from '../queries/generated/GetLocationDetail';
import { getLocationDetail } from '../queries/location-detail.graphql';
import { LocationDetailApiService } from '../services/location-detail-api.service';
import * as LocationDetailActions from './location-detail.actions';
@Injectable()
export class LocationDetailEffects {
   loadLocationDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            LocationDetailActions.locationDetailLoad,
            LocationDetailActions.saveLocationDetailSuccess,
            LocationDetailActions.locationDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);

            this.e911Hub.registerAsE911(id);

            return this.apiService
               .query<GetLocationDetail, GetLocationDetailVariables>({
                  query: getLocationDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([locationResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageLocationSettings_View,
                           locationResult.data.location.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return LocationDetailActions.locationDetailAccessDenied();
                     }
                     return LocationDetailActions.loadLocationDetailSuccess({
                        data: locationResult.data.location,
                     });
                  }),
                  catchError((error) => {
                     return of(LocationDetailActions.loadLocationDetailFailure(error));
                  })
               );
         })
      );
   });

   saveLocationDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(LocationDetailActions.saveLocationDetail),
         switchMap((action) => {
            const apiCall = this.locationDetailApiService.save(action.location, action.locationFields);
            return apiCall.pipe(
               map((result) => {
                  return LocationDetailActions.saveLocationDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(LocationDetailActions.saveLocationDetailFailure(error));
               })
            );
         })
      );
   });

   saveLocationDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(LocationDetailActions.saveLocationDetailSuccess),
            tap(() => {
               this.snackBar.open('Location Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   deleteLocation = createEffect(() => {
      return this.actions$.pipe(
         ofType(LocationDetailActions.deleteLocation),
         switchMap((action) => {
            const message =
               'Are you sure you would like to delete this location? This will remove it from all associated extensions and phone numbers, and disable their outbound dialing.';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Location?',
                  message,
               },
               size: DialogSize.Medium,
            });
            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.locationDetailApiService.deleteLocation(action.locationId).pipe(
                        map(() => {
                           return LocationDetailActions.deleteLocationSuccess(action);
                        }),
                        catchError((error) => {
                           return of(LocationDetailActions.deleteLocationFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteLocationSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(LocationDetailActions.deleteLocationSuccess),
            withLatestFrom(this.store.pipe(select(selectRouteParams))),
            tap(([action, route]) => {
               this.router.navigate([route]);
               this.snackBar.open('Location deleted');
            })
         );
      },
      { dispatch: false }
   );

   e911Notification$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(e911Changed),
         withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
         filter(([_, params, route]) => {
            //Don't reload the page if the user has navigated away from the location
            return (
               route.data?.isDetailPage === true &&
               Array.isArray(route.url) &&
               route.url.some((segment) => segment.path === locationsRoute) &&
               !!params.id
            );
         }),
         concatMap(([_, params]) => {
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetLocationDetail, GetLocationDetailVariables>({
                  query: getLocationDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([locationResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageLocationSettings_View,
                           locationResult.data.location.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return LocationDetailActions.locationDetailAccessDenied();
                     }
                     return LocationDetailActions.loadLocationDetailSuccess({
                        data: locationResult.data.location,
                     });
                  }),
                  catchError((error) => {
                     return of(LocationDetailActions.loadLocationDetailFailure(error));
                  })
               );
         })
      );
   });

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private locationDetailApiService: LocationDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location,
      private e911Hub: E911Hub
   ) {}
}
